export interface PaperInboxReadinessReasonsData {
    identityClaims: boolean;
    authGateRelease: boolean;
    projectMirroring: boolean;
}

export interface PaperInboxReadinessData {
    ready: boolean;
    reasons: PaperInboxReadinessReasonsData;
    pollAfterMs: number;
}

export async function loadAfterPaperInboxReadiness<T>({
    checkReadiness,
    reconcile,
    load,
    delay,
    onSetupChange,
    onPollExhausted,
    onSetupReady,
    onSetupError,
    maxPollAttempts = 8,
}: {
    checkReadiness: () => Promise<PaperInboxReadinessData>;
    reconcile: () => Promise<unknown>;
    load: () => Promise<T>;
    delay: (ms: number) => Promise<void>;
    onSetupChange?: (isSettingUp: boolean) => void;
    onPollExhausted?: (readiness: PaperInboxReadinessData) => void;
    onSetupReady?: () => void | Promise<void>;
    onSetupError?: (error: unknown) => void;
    maxPollAttempts?: number;
}): Promise<T> {
    const loadPromise = load();
    void kickPaperInboxReadinessSetup({
        checkReadiness,
        reconcile,
        delay,
        onSetupChange,
        onPollExhausted,
        onSetupReady: async () => {
            try {
                await loadPromise;
            } catch {
                return;
            }
            await onSetupReady?.();
        },
        onSetupError,
        maxPollAttempts,
    });

    return loadPromise;
}

export async function kickPaperInboxReadinessSetup({
    checkReadiness,
    reconcile,
    delay,
    onSetupChange,
    onPollExhausted,
    onSetupReady,
    onSetupError,
    maxPollAttempts = 8,
}: {
    checkReadiness: () => Promise<PaperInboxReadinessData>;
    reconcile: () => Promise<unknown>;
    delay: (ms: number) => Promise<void>;
    onSetupChange?: (isSettingUp: boolean) => void;
    onPollExhausted?: (readiness: PaperInboxReadinessData) => void;
    onSetupReady?: () => void | Promise<void>;
    onSetupError?: (error: unknown) => void;
    maxPollAttempts?: number;
}): Promise<void> {
    let initialReadiness: PaperInboxReadinessData;
    try {
        initialReadiness = await checkReadiness();
    } catch (error) {
        onSetupError?.(error);
        return;
    }

    if (initialReadiness.ready) {
        return;
    }

    onSetupChange?.(true);

    try {
        await reconcile();

        let latestReadiness = initialReadiness;
        for (let attempt = 0; attempt < maxPollAttempts; attempt += 1) {
            latestReadiness = await checkReadiness();
            if (latestReadiness.ready) {
                break;
            }

            if (attempt < maxPollAttempts - 1) {
                await delay(latestReadiness.pollAfterMs);
            }
        }

        if (!latestReadiness.ready) {
            onPollExhausted?.(latestReadiness);
            return;
        }

        await onSetupReady?.();
    } catch (error) {
        onSetupError?.(error);
    } finally {
        onSetupChange?.(false);
    }
}
